<template>
	<div class="calendar-day" @click="click" :class="[performanceClass, {'blank-day': solveData.blank, 'syncable': syncable, 'syncing': syncing}]" v-tooltip="getTooltip">
    <div class="day-label">{{ puzzleDate.getDate() }}</div>
    <div v-if="solveData.excluded" class="day-status day-excluded">
      <i class="fas fa-star"></i>
    </div>
		<div v-else-if="solveData.solved" class="day-status day-solved">
      <i class="fas fa-star"></i>
    </div>
    <div v-else-if="solveData.completed" class="day-status day-completed">
      <i class="fas fa-star"></i>
    </div>
    <div v-else-if="!solveData.blank" class="day-status day-unknown">
			<i class="fas fa-question"></i>
			<i class="fas fa-sync-alt"></i>
    </div>
		<div v-if="solveData.time" :class="['day-value', 'time-value', { 'time-long': solveData.time >= 3600 }]">{{ displayValue }}</div>
  </div>
</template>
<script>
const axios = require('axios');
const cookies = require('browser-cookies');
import { parseISO, format } from 'date-fns';
import eventBus from '../events';
import store from '../state';

export default {
  name: 'CalendarDay',
  props: {
    day: Object,
  },
	data: function () {
    return {
      syncing: false,
			solveData: {
				solved: this.day.solved,
				completed: this.day.completed,
				blank: this.day.blank,
				time: this.day.time,
				adjustedTime: this.day.adjustedTime,
				date: this.day.date,
				constructor: this.day.constructor,
				checks: this.day.checks,
				reveals: this.day.reveals,
				avgTime: this.day.avgTime,
				avgAdjustedTime: this.day.avgAdjustedTime,
				excluded: this.day.excluded,
				puzzleType: this.day.puzzleType || 'daily',
			}
    }
  },
	methods: {
		click: function() {
			if (this.syncable) {
				this.syncing = true;
				axios({
					method: 'post',
					url: '/api/sync',
					headers: {
						'X-CSRFToken': cookies.get('csrftoken'),
					},
					params: {
						date: format(this.puzzleDate, "MM-d-yyyy"),
						type: this.solveData.puzzleType,
					},
				}).then(resp => {
					this.solveData = {
						...this.solveData,
						...resp.data,
					}
				}).catch(err => {
					if (err.response && err.response.data && err.response.data.error) {
						console.error(err.response.data.msg);
					} else {
						console.error("Uncaught exception");
						console.error(err);
						throw err
					}
				}).finally(() => {this.syncing = false})
			}
		},
	},
  computed: {
    puzzleDate: function() {
			return parseISO(this.solveData.date);
    },
		syncable: function() {
			return !this.solveData.completed && !this.solveData.blank;
		},
		displayMode: function() {
			return store.state.calendarDisplayMode;
		},
		hasASolve: function() {
			return this.solveData.completed && !this.solveData.blank;
		},
		displayValue: function() {
			if (this.displayMode == "checks") {
				return this.solveData.checks + " / " + this.solveData.reveals;
			} else if (this.displayMode == "adjusted") {
				return this.$options.filters.prettyTime(this.solveData.adjustedTime);
			} else {
				return this.$options.filters.prettyTime(this.solveData.time);
			}
		},
		performanceClass: function() {
			if (!this.hasASolve) {
				return ""
			}
			if (this.solveData.excluded) {
				return "performance-excluded"
			}
			if (this.displayMode == "solve") {
				if (this.solveData.avgTime && this.solveData.time < this.solveData.avgTime * 0.8) {
					return "performance-good"
				} else if (this.solveData.avgTime && this.solveData.time > this.solveData.avgTime * 1.2) {
					return "performance-bad"
				}
			} else if (this.displayMode == "adjusted") {
				if (this.solveData.avgAdjustedTime && this.solveData.adjustedTime < this.solveData.avgAdjustedTime * 0.8) {
					return "performance-good"
				} else if (this.solveData.avgAdjustedTime && this.solveData.adjustedTime > this.solveData.avgAdjustedTime * 1.2) {
					return "performance-bad"
				}
			}
			return ""
		},
		puzzleUrl: function() {
			const dateStr = format(this.puzzleDate, "yyyy-MM-dd");
			const type = this.solveData.puzzleType;
			return type === 'daily' ? `/puzzles/${dateStr}` : `/puzzles/${type}/${dateStr}`;
		},
		getTooltip: function() {
			if (!this.hasASolve) {
				return null;
			}
			const content = [
				"Puzzle Date: <b>" + format(this.puzzleDate, "MMM d, yyyy") + "</b>",
				"Constructor: <b>" + this.solveData.constructor + "</b>",
				"Solve Time: <b>" + this.$options.filters.prettyTime(this.solveData.time) + "</b>" + (this.solveData.avgTime ? " (avg " + this.$options.filters.prettyTime(this.solveData.avgTime) + ")" : ""),
				"Checks/Reveals: <b>" + this.solveData.checks + " / " + this.solveData.reveals + "</b>",
				"Adjusted Time: <b>" + this.$options.filters.prettyTime(this.solveData.adjustedTime) + "</b>" + (this.solveData.avgAdjustedTime ? " (avg " + this.$options.filters.prettyTime(this.solveData.avgAdjustedTime) + ")" : ""),
				"",
				"<a href='" + this.puzzleUrl + "'>View Puzzle Summary</a>",
			].join('<br/>');
			return {
				content: content,
				classes: ['tooltip-container'],
				placement: "top",
				autoHide: false,
				trigger: window.innerWidth > 768 ? 'hover focus' : 'click',
				delay: {
					hide: 50,
				},
			}
		}
  },
	mounted: function() {
		// this event gets fired from the global method, sync every day if it happens
		eventBus.on('syncAll', this.click)
	}
};
</script>

<style lang="scss">
.calendar-day.syncable {
	cursor: pointer;
	.day-status .fa-sync-alt {
		display: none;
	}
	&:hover, &.syncing {
		.day-status .fa-question {
			display: none;
		}
		.day-status .fa-sync-alt {
			display: inline;
		}
	}
}

@keyframes rotate{
	to{ transform: rotate(360deg); }
}
.calendar-day.syncing {
	.fa-sync-alt {
		animation: rotate 2s linear infinite;
	}
}

.tooltip {

	display: block !important;
	z-index: 10000;

	.tooltip-inner {
		background: white;
		color: black;
		border-radius: 0.5rem;
		border: 1px solid #999;
		padding: 1rem 2rem;
		box-shadow: 0 5px 30px rgba(black, .1);
		font-size: 0.75rem;
	}

	.tooltip-arrow {
		width: 0;
		height: 0;
		border-style: solid;
		position: absolute;
		margin: 5px;
		border-color: #999;
		z-index: 1;
	}

	&[x-placement^="top"] {

		.tooltip-arrow {
			border-width: 5px 5px 0 5px;
			border-left-color: transparent !important;
			border-right-color: transparent !important;
			border-bottom-color: transparent !important;
			bottom: -5px;
			left: calc(50% - 5px);
			margin-top: 0;
			margin-bottom: 0;
		}
	}

	&.popover {
		$color: #f9f9f9;

		.popover-inner {
			background: $color;
			color: black;
			padding: 24px;
			border-radius: 5px;
			box-shadow: 0 5px 30px rgba(black, .1);
		}

		.popover-arrow {
			border-color: $color;
		}
	}

	&[aria-hidden='true'] {
		visibility: hidden;
		opacity: 0;
		transition: opacity .15s, visibility .15s;
	}

	&[aria-hidden='false'] {
		visibility: visible;
		opacity: 1;
		transition: opacity .15s;
	}
}
</style>
